import * as d3 from 'd3';
import React from "react";
import styled from 'styled-components';

import ChartDimensions from '../../models/ChartDimensions';
import ChartMargins from '../../models/ChartMargins';
import ChartPaddings from '../../models/ChartPaddings';

import Chartable from '../../interfaces/Chartable';
import Scalable from '../../interfaces/Scalable';
import VerticalAxis from './VerticalAxis';
import classNames from 'classnames';

const DIMENSIONS: ChartDimensions = {
  width: 720,
  height: 360,
};

const MARGINS: ChartMargins = {
  top: 10,
  left: 70,
  bottom: 200,
  right: 10,
};

const PADDINGS: ChartPaddings = {
  left: 30,
  right: 30,
};

const StyledBarChart = styled.svg`
  font-family: DMSans-Bold;

  &.dark {
    stroke: #464646;

    text {
      stroke: none;
      fill: #464646;
    }

    & .bar {
      fill: #464646;

      &.mapfre {
        fill: #AE0018;
      }
    }
  }

  &.light {
    stroke: #FBFBFB;

    text {
      stroke: none;
      fill: #FBFBFB;
    }

    & .bar {
      fill: #FFFFFF;

      &.mapfre {
        fill: #AE0018;
      }
    }
  }

  & .bar-label {
    text-anchor: end;
    font-size: 10px;
  }
`;

const StyledBar = styled.rect`
  stroke: none;
`;

export interface SimpleBarData {
  group: string,
  value: number,
}

interface SimpleBarchartProps {
  bars: SimpleBarData[],
  isDark: boolean,
}

interface BarProps extends Scalable, Chartable {
  index: number,
  data: SimpleBarData,
}

const Bar: React.FC<BarProps> = (props: BarProps) => {
  const barWidth = 16;

  return (
    <React.Fragment>
      <StyledBar
          x={ props.xScale(props.index) }
          y={ props.data.value > 0 ? props.yScale(props.data.value) : props.yScale(0) }
          rx={ 6 }
          ry={ 6 }
          width={ barWidth }
          height={ Math.abs(props.yScale(0) - props.yScale(props.data.value)) }
          className={classNames('bar', { 'mapfre': props.data.group === 'MAPFRE' })}
        />
      <text
          className='bar-label'
          x={ props.xScale(props.index) + barWidth / 2 }
          y={ MARGINS.top + DIMENSIONS.height + 10 }
          transform={`rotate(-90,${props.xScale(props.index) + barWidth / 2},${MARGINS.top + DIMENSIONS.height + 10})`}
        >
        { props.data.group }
      </text>
    </React.Fragment>
  )
};

const SimpleBarchart: React.FC<SimpleBarchartProps> = (props: SimpleBarchartProps) => {
  const bars = props.bars.sort((v1, v2) => v2.value - v1.value);
  const values = bars.map(x => x.value);

  const xScale = d3.scaleLinear()
    // @ts-ignore
    .domain([0, bars.length + 1])
    .range([MARGINS.left + PADDINGS.left, MARGINS.left + DIMENSIONS.width - PADDINGS.right]);

  const yScale = d3.scaleLinear()
    // @ts-ignore
    .domain([Math.min(0, d3.min(values)), d3.max(values)])
    .range([MARGINS.top + DIMENSIONS.height, MARGINS.top]);

  const yTicks = yScale.nice().ticks();

  return (
    <StyledBarChart
        viewBox={`0 0 ${DIMENSIONS.width + MARGINS.left + MARGINS.right} ${DIMENSIONS.height + MARGINS.top + MARGINS.bottom}`}
        className={ classNames({
          dark: props.isDark,
          light: !props.isDark
        }) }
      >
      <VerticalAxis
          xScale={ xScale }
          yScale={ yScale }
          ticks={ yTicks }
          dimensions={ DIMENSIONS }
          margins={ MARGINS }
          paddings={ PADDINGS }
          formatTicks={ (n) => `${Math.round(n * 100)}%` }
        />
      {bars.map((bar, i) => {
        return (
          <Bar
              xScale={ xScale }
              yScale={ yScale }
              data={ bar }
              index={ i }
              dimensions={ DIMENSIONS }
              margins={ MARGINS }
              paddings={ PADDINGS }
              key={ bar.group }
            />
        );
      })}
    </StyledBarChart>
  )
};

export default SimpleBarchart;
